// How rare a thing is. The colour that makes it is the client's `rarity.ts`.
//
// **Derived, never declared.** A second table saying which cards are rare is a
// second thing to keep in step with the loot table, and the first time somebody
// rebalances a weight the two disagree in silence — the failure this codebase
// names about `beltsPerTurn`, about `PIECE_SCORE`, about every count standing
// in for a value. So a card's rarity is *how often a pack deals it*, read off
// `packShares`, which is read off the table that decides it.
//
// The border is the whole of the feature: a player who has seen four of a card
// should be able to tell at a glance that the fifth thing is not one of them,
// without reading a word.

import type { CardId } from "src/engine/rules/campaign/cards";
import { packShares } from "src/engine/rules/campaign/loot";
import type { Rarity } from "src/api/chrome";

export type { Rarity };

export const RARITIES: readonly Rarity[] = [
  "common",
  "uncommon",
  "rare",
  "epic",
  "legendary"
];

/** What to call it, for a tooltip. Never drawn as a word beside the border. */
export const RARITY_NAME: Record<Rarity, string> = {
  common: "Common",
  uncommon: "Uncommon",
  rare: "Rare",
  epic: "Epic",
  legendary: "Legendary"
};

/**
 * Where each step begins, as a share of every pack in percent.
 *
 * Read against the shipped table these put the two commonest cards at the
 * bottom, the bulk in the middle two, and the rarest at the top — the shape
 * the pack weights were authored in.
 */
const STEPS: readonly { at: number; rarity: Rarity }[] = [
  { at: 10, rarity: "common" },
  { at: 5, rarity: "uncommon" },
  { at: 2.5, rarity: "rare" },
  { at: 1.2, rarity: "epic" }
];

/** Cached: the table cannot change while the game is running. */
let shares: Map<CardId, number> | null = null;

/**
 * How rare this card is, from its share of every Monarch pack (`MONARCH_PACK`).
 *
 * **Off the table is not common.** A card no pack deals is reached some other
 * way entirely — a level grants it — and the one thing it certainly is not is
 * the thing you see most.
 */
export function rarityOfCard(card: CardId): Rarity {
  shares ??= packShares();
  const share = shares.get(card);
  if (share === undefined) return "legendary";
  for (const step of STEPS) if (share >= step.at) return step.rarity;
  return "legendary";
}

/**
 * How rare a stage offer is.
 *
 * Not a drop rate — nothing rolls for these — so it is read off what makes one
 * *unusual to be offered*: an ordinary camp is what a front normally is,
 * ground worth fighting over is scarcer, and a regrowth is rarest because the board
 * has to be in a particular state before it is on the table at all.
 */
export function rarityOfOffer(offer: {
  kind: "camp" | "regrowth";
  prize?: number;
}): Rarity {
  if (offer.kind === "regrowth") return "legendary";
  if (offer.prize && offer.prize > 0) return "rare";
  return "common";
}
